import type { EdgeType } from "./buildAccessModel";
import type { GraphRect } from "./graphViewport";
import {
  connectorPath,
  orthogonalConnectorPath,
  type ConnectorStyle,
} from "../../../lib/routeConnectors";
import {
  COLUMN_PITCH,
  NODE_HEIGHT,
  NODE_WIDTH,
  ROW_PITCH,
  type NodePosition,
} from "./layoutGraph";

export function edgePath(
  source: NodePosition,
  target: NodePosition,
  style: ConnectorStyle = "loose"
): string {
  const sourceOnLeft = source.x <= target.x;
  const x1 = sourceOnLeft ? source.x + NODE_WIDTH : source.x;
  const y1 = source.y + NODE_HEIGHT / 2;
  const x2 = sourceOnLeft ? target.x : target.x + NODE_WIDTH;
  const y2 = target.y + NODE_HEIGHT / 2;

  if (style === "tidy" && Math.abs(target.col - source.col) > 1) {
    const halfColumnGap = (COLUMN_PITCH - NODE_WIDTH) / 2;
    const sourceTrack = sourceOnLeft ? x1 + halfColumnGap : x1 - halfColumnGap;
    const targetTrack = sourceOnLeft ? x2 - halfColumnGap : x2 + halfColumnGap;
    const rowTrack = source.y + NODE_HEIGHT + (ROW_PITCH - NODE_HEIGHT) / 2;

    return orthogonalConnectorPath([
      [x1, y1],
      [sourceTrack, y1],
      [sourceTrack, rowTrack],
      [targetTrack, rowTrack],
      [targetTrack, y2],
      [x2, y2],
    ]);
  }

  return connectorPath(x1, y1, x2, y2, style);
}

export type EdgeSegment = { d: string; type: EdgeType };

export function edgeBounds(
  source: NodePosition,
  target: NodePosition,
  style: ConnectorStyle
): GraphRect {
  const sourceOnLeft = source.x <= target.x;
  const x1 = sourceOnLeft ? source.x + NODE_WIDTH : source.x;
  const x2 = sourceOnLeft ? target.x : target.x + NODE_WIDTH;
  const y1 = source.y + NODE_HEIGHT / 2;
  const y2 = target.y + NODE_HEIGHT / 2;
  const handle = Math.max(Math.abs(x2 - x1) * 0.5, 24);
  const direction = x1 <= x2 ? 1 : -1;
  const xs =
    style === "loose"
      ? [x1, x2, x1 + direction * handle, x2 - direction * handle]
      : [x1, x2];
  const rowTrack =
    style === "tidy" && Math.abs(target.col - source.col) > 1
      ? source.y + NODE_HEIGHT + (ROW_PITCH - NODE_HEIGHT) / 2
      : y1;
  return {
    left: Math.min(...xs) - 1,
    right: Math.max(...xs) + 1,
    top: Math.min(y1, y2, rowTrack) - 1,
    bottom: Math.max(y1, y2, rowTrack) + 1,
  };
}

// An inherited grant is really two hops: the member reaches the scope only
// because a group they're in has the grant. Route the dashed inherited line to
// that group, then hand off to a solid direct leg for the group's own grant —
// so the group's direct grant is never drawn as if the group had inherited it.
// Falls back to a single straight hop when the group's position is unavailable.
export function inheritedEdgeSegments(
  source: NodePosition,
  group: NodePosition | undefined,
  target: NodePosition,
  style: ConnectorStyle = "loose"
): EdgeSegment[] {
  if (!group)
    return [{ d: edgePath(source, target, style), type: "inherited" }];
  return [
    { d: edgePath(source, group, style), type: "inherited" },
    { d: edgePath(group, target, style), type: "direct" },
  ];
}
